import { GeoPointSchema } from '../location/schemas'

/**
 * A module containing schema validation and documentation related to Devices
 * @module ducks/device/schemas
 */

const SensorType = ['RAIN', 'FLOW']

/** A JSONSchema definition for a get schedules request */
export const GetSchedulesRequest = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    zone_id: {
      type: 'object',
      required: ['id'],
      properties: {
        id: {
          type: 'array',
          items: {
            type: ['string']
          }
        }
      }
    },
    device_id: {
      type: 'object',
      required: ['id'],
      properties: {
        id: {
          type: 'array',
          items: {
            type: ['string']
          }
        }
      }
    } /* eslint-enable camelcase */
  },
  additionalProperties: false
}

/** A JSONSchema definition for an assign proto device request */
export const AssignProtoDeviceRequestSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    user_id: {
      type: 'string',
      minimum: 1
    },
    device_serial: {
      type: 'string',
      minimum: 1
    }
    /* eslint-enable camelcase */
  },
  required: ['device_serial']
}

export const UpdateIrrigationControllerRequest = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    id: {
      // string id = 1;
      type: 'string',
      minimum: 1
    },
    name: {
      // google.protobuf.StringValue name = 2;
      type: 'string'
    },
    geo_point: GeoPointSchema, // GeoPoint geo_point = 3;
    location_id: {
      // google.protobuf.StringValue location_id = 5;
      type: 'string'
    },
    master_valve: {
      // google.protobuf.BoolValue master_valve = 6;
      type: 'boolean'
    },
    settle_time: {
      // google.protobuf.BoolValue standby = 7;
      type: 'number'
    },
    standby: {
      // google.protobuf.BoolValue standby = 7;
      type: 'boolean'
    },
    water_hammer: {
      // google.protobuf.BoolValue water_hammer = 8;
      type: 'boolean'
    },
    add_photo_bytes: {
      // repeated google.protobuf.BytesValue add_photo_bytes = 9;
      type: 'array',
      items: {
        type: ['string', 'null']
      }
    },
    remove_photo_id: {
      // repeated string remove_photo_id = 10;
      type: 'string'
    },
    wellpump_delay_active: {
      type: 'boolean'
    }
    /* eslint-enable camelcase */
  },
  required: ['id'],
  additionalProperties: false
}

export const SetLightBarSettingRequest = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    device_id: {
      // string id = 1;
      type: 'string',
      minimum: 1
    },
    light_bar_setting: {
      // LightBarSetting light_bar_setting = 2;
      type: 'string'
    }
  },
  required: ['device_id'],
  additionalProperties: false
}

export const UpdateLinkedSensorRequest = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    id: {
      // string id = 1;
      type: 'string',
      minimum: 1
    },
    make: {
      //google.protobuf.StringValue make = 2;
      type: 'string',
      minimum: 1
    },
    model: {
      //google.protobuf.StringValue model = 3;
      type: 'string',
      minimum: 1
    },
    kfactor: {
      // google.protobuf.DoubleValue kfactor = 4;
      type: 'number'
    },
    offset: {
      // google.protobuf.DoubleValue offset = 5;
      type: 'number'
    },
    enabled: {
      // bool enabled = 6;
      type: 'boolean'
    },
    sensor_type: {
      // LinkedSensorType sensorType = 7;
      type: 'string',
      enum: SensorType
    },
    linked_controller_id: {
      // string linked_controller_id = 8;
      type: 'string',
      minimum: 1
    }
    /* eslint-enable camelcase */
  },
  required: ['id'],
  additionalProperties: false
}

// One of device_id string array OR zone_id string array
// message GetActiveAlertsRequest {
//   oneof entity_id_list {
//     StringList device_id = 1;
//     StringList zone_id = 2;
//   }
// }
export const GetActiveAlertsRequest = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    device_id: {
      type: 'object',
      required: ['id'],
      properties: {
        id: {
          type: 'array',
          items: {
            type: ['string']
          }
        }
      }
    },
    zone_id: {
      type: 'object',
      required: ['id'],
      properties: {
        id: {
          type: 'array',
          items: {
            type: ['string']
          }
        }
      }
    }
    /* eslint-enable camelcase */
  },
  additionalProperties: false
}

// One of device_id string OR zone_id string
export const DismissAlertsRequest = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    device_id: {
      type: 'string'
    },
    zone_id: {
      type: 'string'
    },
    alert_ids: {
      type: 'array',
      items: {
        type: ['string']
      }
    }
    /* eslint-enable camelcase */
  },
  required: ['alert_ids'],
  additionalProperties: false
}

/**
 * Schema for transfer ownership request
 * @see {@link https://github.com/Rachio/proto/blob/5a7fe1aa998c53a52150ff66cd088e6651722c09/user_service.proto#L290}
 */
export const TransferOwnershipRequest = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    device_id: {
      type: 'string'
    },
    grant_shared_access: {
      type: 'boolean'
    },
    grant_shared_access_existing_users: {
      type: 'boolean'
    },
    to_email: {
      type: 'string'
    },
    user_id: {
      type: 'string'
    }
    /* eslint-enable camelcase */
  },
  required: ['device_id', 'to_email', 'user_id'],
  additionalProperties: false
}
